import {useCallback, useEffect, useRef, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {getToken} from '@/utils/authToken'
import {
    acquireReverb,
    releaseReverb,
    setReverbHandlers,
    subscribeTripChannel,
    unsubscribeTripChannel,
} from '@/services/reverb'

/**
 * WHERE THE DRIVER IS, live, for one trip.
 *
 * The passenger's answer to the driver portal's `useDriverLocation`. That one reads the
 * device's own GPS; this one has no device to read, so it holds a socket open and takes
 * what the backend broadcasts.
 *
 * ── ⚠️ THERE IS NO FIRST POSITION ─────────────────────────────────────────────────
 * Nothing replays the driver's last known fix — not the trip resource, not any endpoint.
 * So this returns `null` from mount until the driver's next ping lands, which is 3 to 10
 * seconds away at best and never at worst. That is not a loading state to be papered over
 * with a spinner: it is a real condition the page has to have a screen for, and it is why
 * `TrackDriver` renders a waiting state rather than an empty map.
 */

/**
 * THE LAST FIX, KEPT ON THIS DEVICE.
 *
 * ⚠️ BECAUSE THE BACKEND DOES NOT HOLD ONE. `TripResource.driver` carries a name, a
 * phone, a photo and a vehicle, and no coordinates; the only location endpoints accept a
 * position rather than return one. So on a reload there is genuinely nothing to ask, and
 * the page that had a car a moment ago comes back with an empty map.
 *
 * ⚠️ AN EMPTY MAP IS THE WORSE LIE. A pin sitting still is a car that may not have
 * moved, or a driver whose phone locked — and the staleness pill says which. Nothing at
 * all reads as the tracking being broken, which is the one impression this page cannot
 * afford. A remembered position is shown as exactly what it is: stale from the moment it
 * is restored, because `receivedAt` is restored with it.
 *
 * ⚠️ AND IT IS PER TRIP, AND IT EXPIRES. A yesterday's ride replayed onto today's map
 * would be worse than nothing, so anything older than this is discarded unread.
 */
const CACHE_KEY = (tripId) => `hopn.driverPosition.v1.${tripId}`
/**
 * ⚠️ TWO MINUTES, DOWN FROM TWELVE HOURS.
 *
 * Twelve hours was wrong and it showed. A remembered position is drawn on the first frame,
 * before the socket has connected — so a reload put the car wherever it was LAST time,
 * which after any real gap is somewhere it is not, and the car then jumped across the map
 * when the first live ping landed. That jump is exactly what a broken map looks like, and
 * it is worse than the empty one this was meant to replace.
 *
 * ⚠️ AND THE SESSION HOLD IS WHAT ACTUALLY ANSWERS "THE DRIVER STOPPED SENDING". That
 * is handled in memory, and it never expires. This cache only ever covers a RELOAD, where
 * a two-minute-old position is still roughly where the car is and anything older is a
 * guess. Past it, "waiting for your driver's location" is the honest answer.
 */
const CACHE_MAX_AGE_MS = 2 * 60 * 1000

function readCachedPosition(tripId) {
    if (!tripId) return null
    try {
        const raw = window.localStorage.getItem(CACHE_KEY(tripId))
        if (!raw) return null
        const {lat, lng, heading, at} = JSON.parse(raw)
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(at)) return null
        if (Date.now() - at > CACHE_MAX_AGE_MS) return null
        return {position: {lat, lng, heading: Number.isFinite(heading) ? heading : null}, at}
    } catch {
        // Private mode, storage off, or something an older build wrote.
        return null
    }
}

function writeCachedPosition(tripId, position, at) {
    if (!tripId) return
    try {
        window.localStorage.setItem(CACHE_KEY(tripId), JSON.stringify({...position, at}))
    } catch {
        // Losing the memory costs a reload's worth of pin, and nothing else.
    }
}

/**
 * How long a fix stays believable, in ms.
 *
 * ⚠️ A DRIVER ON THE WEB APP STOPS SENDING WHEN THEIR TAB IS HIDDEN or their phone locks —
 * there is no background GPS on the web. The pin then sits exactly where it was, looking
 * live, while the car drives away from it. A passenger deciding whether to walk outside
 * deserves to know the difference. 30s is three missed pings at the slow end of the 3-10s
 * cadence, so it does not cry wolf over one dropped frame.
 */
const STALE_AFTER_MS = 30000

/** Re-evaluate staleness on a timer, since nothing arrives to trigger a render. */
const STALE_TICK_MS = 5000

export function useDriverPosition(tripId, {enabled = true} = {}) {
    const queryClient = useQueryClient()
    /* ⚠️ SEEDED FROM THE CACHE, NOT NULL. Lazy initialisers, so the restore happens once
       on mount rather than on every render — and so the first paint already has the car
       rather than flashing the waiting state and then filling it in. */
    const [position, setPosition] = useState(() => readCachedPosition(tripId)?.position || null)
    const [receivedAt, setReceivedAt] = useState(() => readCachedPosition(tripId)?.at || null)
    const [now, setNow] = useState(() => Date.now())

    /* ⚠️ NO DEPENDENCIES, so this identity never changes. It only calls setters, and a
       handler that was rebuilt every render would re-run the registration effect below at
       render rate — which is the churn the ref-during-render version was reaching for and
       got wrong. */
    const onDriverLocation = useCallback((payload) => {
        const lat = Number(payload?.lat)
        const lng = Number(payload?.lng)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return

        /*
         * ⚠️ `bearing`, NOT `heading`. The wire name differs from the one every map
         * component here uses, and the units are the same — degrees clockwise from north.
         * It is nullable, and null must stay null rather than become 0: `RouteMap` treats
         * a finite heading as the truth about which way the car points, and a "no idea"
         * arriving as a confident due-north pins the car facing up the screen through
         * every turn. The driver portal documents that exact bug from its own testing.
         */
        const bearing = Number(payload?.bearing)
        const next = {lat, lng, heading: Number.isFinite(bearing) ? bearing : null}
        const at = Date.now()
        setPosition(next)
        setReceivedAt(at)
        /* ⚠️ WRITTEN ON EVERY FIX, not on unload. `beforeunload` does not fire reliably on
           a phone — a tab killed by the OS, a browser closed from the app switcher, a
           crash — and those are exactly the cases this exists for. A localStorage write
           every few seconds is cheap. */
        writeCachedPosition(tripIdRef.current, next, at)
    }, [])

    /* The handler above has no dependencies on purpose — see its own note — so the trip it
       is writing for is read through a ref rather than closed over. */
    const tripIdRef = useRef(tripId)
    useEffect(() => { tripIdRef.current = tripId })

    const onTripStatus = useCallback(() => {
        /* ⚠️ A HINT TO REFETCH, NOT THE STATE ITSELF. The trip is already polled every two
           seconds by `useTripDetails`; this only shortens the wait when the driver starts
           or ends the ride. Patching a status straight into the cache from a socket
           payload is how two sources of truth start disagreeing. */
        queryClient.invalidateQueries({queryKey: ['trip', tripId]})
    }, [queryClient, tripId])

    /*
     * ⚠️ THE HANDLERS ARE REGISTERED IN THEIR OWN EFFECT, not inside the connection one.
     *
     * `setReverbHandlers` replaces the service's whole handler object, and the driver
     * portal records what happens when the two are combined: the connection effect returns
     * early when it is already connected, so the registration never runs and the callbacks
     * sit undefined on a perfectly healthy socket. Events arrive and go nowhere, with
     * nothing in the console to say so. Whether the socket is open and what should happen
     * when it delivers are two different questions and get two different effects.
     */
    useEffect(() => {
        setReverbHandlers({onDriverLocation, onTripStatus})
    }, [onDriverLocation, onTripStatus])

    useEffect(() => {
        if (!enabled || !tripId) return undefined
        const token = getToken()
        if (!token) return undefined
        acquireReverb({token})
        subscribeTripChannel(tripId)

        return () => {
            unsubscribeTripChannel()
            /* ⚠️ RELEASED, NOT DISCONNECTED. This used to close the whole socket, on the
               grounds that this hook was the portal's only realtime consumer. It no longer
               is — the layout holds the passenger's user channel open on every page — so
               this gives up its own hold and the socket closes only when nobody else has
               one. See `acquireReverb`. */
            releaseReverb()
        }
    }, [enabled, tripId])

    /* Only while a fix is being held — a page that has never had one has nothing to go
       stale, and a timer running on the waiting screen would be pure wake-ups. */
    useEffect(() => {
        if (!receivedAt) return undefined
        const timer = setInterval(() => setNow(Date.now()), STALE_TICK_MS)
        return () => clearInterval(timer)
    }, [receivedAt])

    /*
     * ⚠️ THE LAST KNOWN POSITION IS NEVER GIVEN UP, AND THIS RETURN IS WHERE THAT IS
     * GUARANTEED. Nothing above clears `position`, but the guarantee was implicit — it
     * held because no code path happened to null it, which is the kind of thing a later
     * change breaks silently. A passenger whose car vanishes reads it as the tracking
     * being broken; a car sitting still with "last updated 6 min ago" under it reads as a
     * driver whose phone went to sleep, which is both true and far less alarming.
     *
     * ⚠️ STALENESS IS THE HONEST PART OF THIS, so it is computed from the same
     * `receivedAt` rather than being reset. The pin holds; the label ages.
     */
    const lastKnown = useRef(null)
    if (position) lastKnown.current = {position, receivedAt}
    const heldPosition = position || lastKnown.current?.position || null
    const heldAt = receivedAt || lastKnown.current?.receivedAt || null

    return {
        position: heldPosition,
        receivedAt: heldAt,
        hasPosition: !!heldPosition,
        isStale: !!heldAt && now - heldAt > STALE_AFTER_MS,
        secondsSince: heldAt ? Math.round((now - heldAt) / 1000) : null,
    }
}

export default useDriverPosition
